import axios from '@/lib/axios'
import { useGlobalErrorsStore } from '@/stores/globalErrors'
import { useHotelRateStore } from '@/stores/hotelRate'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { request } from 'http'
import { set } from 'lodash'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'
import { LocationQuery, Router, useRoute, useRouter } from 'vue-router'
import { ToastInterface, useToast } from 'vue-toastification'

import useConfirmations from '@/modules/confirmations/composables/useConfirmations'

import { HotelConfirmationResponse } from '../../confirmations/interfaces/hotelConfirmationResponse'
import { HotelRateResponse } from '../interfaces/hotelRateReponse'
import { PaymentInfo } from '../interfaces/paymentInfo'

interface Props {
	immediate?: boolean
}

const createBooking = async (
	payload: any,
	setHotelConfirmations: (
		requests: HotelConfirmationResponse[],
		isManual?: boolean,
		message?: string,
		paymentInfo?: PaymentInfo
	) => void,
	router?: Router
) => {
	// Al volver de 3DS se reinicia la SPA y ya no está el proveedor en hotelRate.
	// HBX puede tardar hasta 90 s en confirmar; este límite también cubre el cobro.
	const { data, status } = await axios.post('/api/pnrs/create', payload, {
		timeout: 180_000,
	})

	if (data.url) {
		window.location.href = data.url
		return
	}

	if (status === 200) {
		setHotelConfirmations(data.requests, false, data.message, data.paymentInfo)
		sessionStorage.setItem(
			'hotelBookingResult',
			JSON.stringify({
				requests: data.requests,
				message: data.message,
				paymentInfo: data.paymentInfo,
			})
		)
	}
}

const getHotelRate = async (
	payload: LocationQuery,
	toast: ToastInterface,
	router: Router,
	setErrorMessage: (message: string) => void = () => {}
): Promise<HotelRateResponse> => {
	try {
		const { data, status } = await axios.post<HotelRateResponse>(
			'/api/hotels/rate',
			{
				...payload,
				users: JSON.parse(payload.users as string) as string[],
			}
		)

		if (!('rate' in data)) {
			setErrorMessage('No se encontró tarifa para la búsqueda realizada.')
			router.back()
		}

		return data
	} catch (err: any) {
		const data = err.response?.data

		// El precio cambió respecto a lo que el cliente vio (guard PRICE_MISMATCH) o la
		// oferta expiró: en vez de dejar un error crudo, damos feedback claro con el
		// precio nuevo y regresamos al detalle, que recarga la disponibilidad vigente.
		if (data?.code === 'PRICE_MISMATCH') {
			const nuevo = Number(data.current_fare).toLocaleString('es-MX', {
				minimumFractionDigits: 2,
			})
			setErrorMessage(
				`La tarifa cambió a $${nuevo} ${data.currency ?? 'MXN'}. Actualizamos la disponibilidad; revisa el nuevo precio antes de reservar.`
			)
		} else if (data?.code === 'OFFER_EXPIRED') {
			setErrorMessage(
				'Tu tarifa expiró. Actualizamos la disponibilidad para mostrarte el precio vigente.'
			)
		} else {
			setErrorMessage(data?.message || 'Error al obtener la tarifa del hotel')
		}

		router.back()
		throw err
	}
}

const useHotelBook = (props?: Props) => {
	const {
		setHotelRate,
		setPayloadValue,
		fillFormValues,
		fillPayloadValues,
		setBillingInformation,
		setCompanyBilling,
		setRequireInvoice,
		setMandatoryCostCenter,
		setPolicyInformation,
		resetBillingData,
	} = useHotelRateStore()
	const {
		hotelRate,
		form,
		payload,
		billingInformation,
		companyBilling,
		requireInvoice,
		mandatoryCostCenter,
		policyInformation,
		policyErrorFlag,
	} = storeToRefs(useHotelRateStore())
	const { immediate = true } = props || {}
	const { query } = useRoute()
	const router = useRouter()
	const { setHotelConfirmations } = useConfirmations()
	const toast = useToast()
	const { setError } = useGlobalErrorsStore()

	const { data, isLoading, refetch, isFetching } = useQuery(
		['hotelRate'],
		async () => {
			if (query.id) {
				const payload = JSON.parse(
					localStorage.getItem('hotelRatePayload') ?? ''
				)

				const pay = {
					...payload,
					paymentId: query.id,
				}

				try {
					await createBooking(pay, setHotelConfirmations, router)

					router.push({
						name: 'hotel.confirmation',
					})
					toast.success('Su reservación y pago han sido realizado con éxito.')
				} catch (error: any) {
					setError(
						error.response.data.message || 'Error al crear la reservación'
					)

					const queryParams = {
						Start: query.Start,
						End: query.End,
						Quantity: query.Quantity,
						GuestCount: query.GuestCount,
						IsPerRoom: Number(query.IsPerRoom),
						HotelCode: query.HotelCode,
						users: query.users,
						memberId: query.memberId,
						hotelName: query.hotelName,
						// Volver al detalle sigue siendo la misma búsqueda.
						hotelSearchId: query.hotelSearchId,
					}

					router.push({
						name: 'hotel.details',
						query: { ...queryParams },
					})
				} finally {
					// localStorage.removeItem("hotelRatePayload");
				}
			} else {
				return getHotelRate(query, toast, router, setError)
			}
		},
		{
			retry: false,
			enabled: immediate,
			refetchOnReconnect: false,
			refetchOnWindowFocus: false,
		}
	)

	const bookingMutation = useMutation(
		() => createBooking(payload.value, setHotelConfirmations, router),
		{
			onError: (err: any) => {
				const data = err?.response?.data
				// Oferta/tarifa expirada al momento de reservar (el usuario tardó): en vez
				// de dejar un error crudo, recargamos la disponibilidad vigente y avisamos.
				if (data?.code === 'OFFER_EXPIRED') {
					toast.warning(
						'Tu tarifa expiró. Actualizamos la disponibilidad para mostrarte el precio vigente.'
					)
					refetch()
					return
				}
				setError(data?.message || 'No se pudo completar la reservación')
			},
		}
	)

	watch(
		data,
		(value) => {
			if (value) {
				setHotelRate(value.rate)
				const { errors, warnings } = value
				if (errors || warnings) setPolicyInformation({ errors, warnings })
			}
		},
		{ immediate: true }
	)

	return {
		hotelRate,
		ratePayload: payload,
		rateForm: form,
		billingInformation,
		companyBilling,
		requireInvoice,
		isLoading,
		isFetching,
		bookingMutation,
		mandatoryCostCenter,
		policyInformation,
		policyErrorFlag,

		setPayloadValue,
		setHotelRate,
		setBillingInformation,
		setCompanyBilling,
		setRequireInvoice,
		setMandatoryCostCenter,
		setPolicyInformation,
		resetBillingData,
		fillFormValues,
		fillPayloadValues,
		refetch,

		createBooking: bookingMutation.mutateAsync,
		creatingBooking: bookingMutation.isLoading,
		returnToDetails: () => {
			const queryParams = {
				Start: query.Start,
				End: query.End,
				Quantity: query.Quantity,
				GuestCount: query.GuestCount,
				IsPerRoom: Number(query.IsPerRoom),
				HotelCode: query.HotelCode,
				users: query.users,
				memberId: query.memberId,
				hotelName: query.hotelName,
				// Volver al detalle sigue siendo la misma búsqueda.
				hotelSearchId: query.hotelSearchId,
			}

			router.push({
				name: 'hotel.details',
				query: { ...queryParams },
			})
		},
	}
}

export default useHotelBook
